import React from "react";

import {
  formatHarvestLossRate,
  harvestLossMachineLabel,
} from "./harvestLossMetrics";

import "./FieldAppHarvestLossSpreadsheet.css";

function formatSampleTimestamp(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("pt-BR");
}

function formatSampleLocation(sample) {
  if (sample.latitude == null || sample.longitude == null) return "—";
  return `${Number(sample.latitude).toFixed(6)}, ${Number(sample.longitude).toFixed(6)}`;
}

function SummaryMetricRows({ machineLabel, summary, t }) {
  if (!summary || !summary.sampleCount) return null;

  const { lastSample, avgLast3, avgTotal } = summary;
  const rollingWindow = summary.rollingWindow;
  const rollingLabel =
    rollingWindow && rollingWindow > 1
      ? t("Média últimas {{count}}", { count: rollingWindow })
      : null;

  return (
    <>
      {lastSample ? (
        <tr>
          <td>{machineLabel}</td>
          <td>{t("Última amostra")}</td>
          <td>{Math.round(lastSample.seedCount).toLocaleString("pt-BR")}</td>
          <td>{formatHarvestLossRate(lastSample.kgPerHa, t("kg/ha"))}</td>
          <td>{formatHarvestLossRate(lastSample.bagsPerHa, t("sc/ha"), 3)}</td>
        </tr>
      ) : null}
      {rollingLabel ? (
        <tr>
          <td>{machineLabel}</td>
          <td>{rollingLabel}</td>
          <td>
            {avgLast3.seedCount != null
              ? Math.round(avgLast3.seedCount).toLocaleString("pt-BR")
              : "—"}
          </td>
          <td>{formatHarvestLossRate(avgLast3.kgPerHa, t("kg/ha"))}</td>
          <td>{formatHarvestLossRate(avgLast3.bagsPerHa, t("sc/ha"), 3)}</td>
        </tr>
      ) : null}
      <tr>
        <td>{machineLabel}</td>
        <td>{t("Média total do período")}</td>
        <td>
          {avgTotal.seedCount != null
            ? Math.round(avgTotal.seedCount).toLocaleString("pt-BR")
            : "—"}
        </td>
        <td>{formatHarvestLossRate(avgTotal.kgPerHa, t("kg/ha"))}</td>
        <td>{formatHarvestLossRate(avgTotal.bagsPerHa, t("sc/ha"), 3)}</td>
      </tr>
    </>
  );
}

function SampleTableRow({ sample, onOpenMap, t }) {
  const locationLabel = formatSampleLocation(sample);
  const canOpenMap = locationLabel !== "—" && onOpenMap;

  return (
    <tr>
      <td>{harvestLossMachineLabel(sample.machineName || "", t)}</td>
      <td>{sample.repetitionNumber != null ? sample.repetitionNumber : "—"}</td>
      <td>{formatSampleTimestamp(sample.timestamp)}</td>
      <td>{sample.operatorName || "—"}</td>
      <td>{sample.seedCount != null ? sample.seedCount.toLocaleString("pt-BR") : "—"}</td>
      <td>{formatHarvestLossRate(sample.kgPerHa, t("kg/ha"))}</td>
      <td>{formatHarvestLossRate(sample.bagsPerHa, t("sc/ha"), 3)}</td>
      <td>
        {canOpenMap ? (
          <button
            type="button"
            className="field-app-harvest-loss-spreadsheet-map-btn"
            title={t("Abrir mapa e telemetria neste ponto")}
            onClick={() => onOpenMap(sample)}
          >
            {locationLabel}
          </button>
        ) : (
          locationLabel
        )}
      </td>
      <td>{sample.notes || "—"}</td>
    </tr>
  );
}

export default function FieldAppHarvestLossSpreadsheet({
  machineBreakdown,
  samples,
  harvestConfig,
  orderLabel,
  onOpenMap,
  t,
}) {
  const showOverallTotal =
    machineBreakdown &&
    machineBreakdown.machines &&
    machineBreakdown.machines.length > 1 &&
    machineBreakdown.overall &&
    machineBreakdown.overall.sampleCount;

  return (
    <div className="field-app-harvest-loss-spreadsheet">
      <div className="field-app-harvest-loss-spreadsheet__print-header">
        <h3 className="field-app-harvest-loss-spreadsheet__title">
          {t("Planilha de perda de colheita")}
        </h3>
        {orderLabel ? (
          <p className="field-app-harvest-loss-spreadsheet__meta">{orderLabel}</p>
        ) : null}
        {harvestConfig ? (
          <p className="field-app-harvest-loss-spreadsheet__meta">
            {t("PMS")}: {harvestConfig.pmsG} g
            {harvestConfig.toleratedLossKgHa != null
              ? ` · ${t("Margem tolerada")}: ${formatHarvestLossRate(
                  harvestConfig.toleratedLossKgHa,
                  t("kg/ha")
                )}`
              : ""}
          </p>
        ) : null}
      </div>

      {machineBreakdown && machineBreakdown.machines && machineBreakdown.machines.length ? (
        <div className="field-app-harvest-loss-spreadsheet__section">
          <h4>{t("Médias por máquina")}</h4>
          <table className="table table-striped jambo_table field-app-harvest-loss-spreadsheet__table">
            <thead>
              <tr>
                <th>{t("Máquina")}</th>
                <th>{t("Métrica")}</th>
                <th>{t("Sementes")}</th>
                <th>{t("kg/ha")}</th>
                <th>{t("sc/ha")}</th>
              </tr>
            </thead>
            <tbody>
              {machineBreakdown.machines.map((machine) => (
                <SummaryMetricRows
                  key={machine.machineKey}
                  machineLabel={harvestLossMachineLabel(machine.machineKey, t)}
                  summary={machine.summary}
                  t={t}
                />
              ))}
              {showOverallTotal ? (
                <SummaryMetricRows
                  machineLabel={t("Total geral (todas as máquinas)")}
                  summary={machineBreakdown.overall}
                  t={t}
                />
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}

      {samples && samples.length ? (
        <div className="field-app-harvest-loss-spreadsheet__section">
          <h4>{t("Amostras registradas")}</h4>
          <table className="table table-striped jambo_table field-app-harvest-loss-spreadsheet__table field-app-harvest-loss-spreadsheet__table--samples">
            <thead>
              <tr>
                <th>{t("Máquina")}</th>
                <th>{t("Rep.")}</th>
                <th>{t("Data/Hora")}</th>
                <th>{t("Operador")}</th>
                <th>{t("Sementes")}</th>
                <th>{t("kg/ha")}</th>
                <th>{t("sc/ha")}</th>
                <th>{t("GPS")}</th>
                <th>{t("Obs.")}</th>
              </tr>
            </thead>
            <tbody>
              {samples.map((sample) => (
                <SampleTableRow
                  key={sample.uuid || `${sample.repetitionNumber}-${sample.timestamp}`}
                  sample={sample}
                  onOpenMap={onOpenMap}
                  t={t}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
